/**
 * ValuesTestAccelerometer - Componente de acelerómetro genérico para múltiples tests
 * 
 * Este componente renderiza acelerómetros visuales que muestran los resultados de cualquier test
 * para un candidato específico. Es configurable para diferentes tipos de tests y categorías.
 * 
 * Características principales:
 * - Arco semicircular con gradiente de colores configurable
 * - Aguja indicadora que se posiciona según el puntaje calculado
 * - Cálculo automático de puntajes por categoría
 * - Sistema de rangos configurable (0-1 Verde, 2-3 Amarillo, 4-6 Rojo)
 * - Feedback personalizable por test y rango
 * 
 * @author Ana Lizcano
 * @version 2.0.0
 */

import { useMemo } from "react";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Grid from "@mui/material/Grid";

// Configuraciones centralizadas
import { getTestConfiguration } from "config/testConfigurations";

/**
 * Procesa los resultados del test de valores y calcula puntajes por categoría
 * @param {Array} respuestas - Array de respuestas del candidato
 * @returns {Object} Puntajes calculados para cada categoría
 */
const processValuesTestData = (respuestas) => {
  if (!respuestas || !Array.isArray(respuestas)) {
    return {
      "Principios Eticos": 0,
      "Conformidad Normativa": 0,
      "Apatia Social": 0,
      "Tendencia a la Corrupcion": 0
    };
  }

  const categoryScores = {
    "Principios Eticos": 0,
    "Conformidad Normativa": 0,
    "Apatia Social": 0,
    "Tendencia a la Corrupcion": 0
  };

  respuestas.forEach(respuesta => {
    if (categoryScores.hasOwnProperty(respuesta.categoria)) {
      categoryScores[respuesta.categoria] += respuesta.valor_asignado;
    }
  });

  return categoryScores;
};

/**
 * Procesa los resultados del test de canciones
 * @param {Object} songTestData - Datos del test de canciones con estructura {data: {puntaje_total: number}}
 * @returns {Object} Puntajes calculados para la categoría "Resonancia Emocional"
 */
const processSongTestData = (songTestData) => {
  if (!songTestData?.data?.puntaje_total && songTestData?.data?.puntaje_total !== 0) {
    return {
      "Resonancia Emocional": 0
    };
  }

  return {
    "": songTestData.data.puntaje_total
  };
};

/**
 * Procesa los resultados del test de dibujos
 * @param {Object} drawingTestData - Datos del test de dibujos con estructura {responses: [{total_score: number, classification_description: string}]}
 * @returns {Object} Puntajes calculados para la categoría "Test de Dibujos"
 */
const processDrawingTestData = (drawingTestData) => {
  if (!drawingTestData?.responses?.[0]?.total_score && drawingTestData?.responses?.[0]?.total_score !== 0) {
    return {
      "": 0
    };
  }

  return {
    "": drawingTestData.responses[0].total_score
  };
};

/**
 * Componente individual del acelerómetro para una categoría
 */
const SingleAccelerometer = ({ title, score, maxScore, feedbackConfig, isInverseCategory = false, backendInterpretation = null }) => {
  const needleAngle = useMemo(() => {
    let percentage = Math.min(Math.max(score / maxScore, 0), 1);
    
    // Para categorías inversas, invertir la posición de la aguja
    if (isInverseCategory) {
      percentage = 1 - percentage;
    }
    
    return percentage * 180;
  }, [score, maxScore, isInverseCategory]);

  const needleColor = useMemo(() => {
    let percentage = score / maxScore;
    
    // Para categorías inversas, invertir la lógica de colores
    if (isInverseCategory) {
      percentage = 1 - percentage;
    }
    
    if (percentage < 0.33) return "#dc3545";
    if (percentage < 0.66) return "#ffc107";
    return "#28a745";
  }, [score, maxScore, isInverseCategory]);

  const getDescription = (category, scoreValue, maxScoreValue, feedbackConfig, isInverse = false) => {
    let normalizedScore = (scoreValue / maxScoreValue) * 6;
    
    // Para categorías inversas, invertir el score normalizado para el feedback
    if (isInverse) {
      normalizedScore = 6 - normalizedScore;
    }
    
    let rangeKey;
    if (normalizedScore <= 1) {
      rangeKey = "verde";
    } else if (normalizedScore <= 3) {
      rangeKey = "amarillo";
    } else {
      rangeKey = "rojo";
    }
    
    if (feedbackConfig && feedbackConfig[category] && feedbackConfig[category][rangeKey]) {
      return feedbackConfig[category][rangeKey];
    }
    
    return "Sin descripción disponible para esta categoría y rango";
  };

  return (
    <MDBox sx={{ 
      backgroundColor: '#f8f9fa', 
      padding: '20px', 
      borderRadius: '8px',
      textAlign: 'center',
      minHeight: '300px'
    }}>
      <MDTypography variant="body1" fontWeight="bold" color="dark" mb={2}>
        {title}
      </MDTypography>

      <MDBox sx={{ 
        position: 'relative', 
        width: '200px', 
        height: '120px',
        margin: '20px auto'
      }}>
        <svg width="200" height="120" style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id={`gradient-${title.replace(/\s+/g, '')}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#dc3545" />
              <stop offset="50%" stopColor="#ffc107" />
              <stop offset="100%" stopColor="#28a745" />
            </linearGradient>
          </defs>
          
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            stroke={`url(#gradient-${title.replace(/\s+/g, '')})`}
            strokeWidth="12"
            fill="none"
            strokeLinecap="round"
          />
          
          {[0, 45, 90, 135, 180].map((angle, index) => {
            const radian = (angle * Math.PI) / 180;
            const x1 = 100 + 75 * Math.cos(Math.PI - radian);
            const y1 = 100 - 75 * Math.sin(Math.PI - radian);
            const x2 = 100 + 85 * Math.cos(Math.PI - radian);
            const y2 = 100 - 85 * Math.sin(Math.PI - radian);
            
            return (
              <line
                key={index}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#333"
                strokeWidth="2"
              />
            );
          })}
          
          <g transform={`translate(100, 100) rotate(${needleAngle - 90})`}>
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="-70"
              stroke={needleColor}
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle
              cx="0"
              cy="0"
              r="4"
              fill={needleColor}
            />
          </g>
        </svg>
      </MDBox>

      <MDBox sx={{ 
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '4px',
        border: `2px solid ${needleColor}`,
        marginBottom: '12px',
        display: 'inline-block'
      }}>
        <MDTypography variant="body1" fontWeight="bold" color="dark">
          {score}/{maxScore}
        </MDTypography>
      </MDBox>

      <MDTypography variant="body2" color="text" textAlign="justify" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
        {backendInterpretation || getDescription(title, score, maxScore, feedbackConfig, isInverseCategory)}
      </MDTypography>
    </MDBox>
  );
};

SingleAccelerometer.propTypes = {
  title: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  maxScore: PropTypes.number.isRequired,
  feedbackConfig: PropTypes.object,
  isInverseCategory: PropTypes.bool,
  backendInterpretation: PropTypes.string
};

/**
 * Componente principal del acelerómetro genérico para tests
 */
function ValuesTestAccelerometer({ 
  testData, 
  testType = "values", // "values" or "songs"
  testConfig = null // Configuración personalizada opcional
}) {
  // Obtener configuración centralizada o usar la personalizada
  const config = useMemo(() => {
    const defaultConfig = getTestConfiguration(testType);
    
    if (!testConfig) {
      return defaultConfig;
    }
    
    // Mezclar configuración personalizada con la por defecto
    return {
      title: testConfig.title || defaultConfig.title,
      subtitle: testConfig.subtitle || defaultConfig.subtitle,
      categories: testConfig.categories || defaultConfig.categories,
      maxScore: testConfig.maxScore || defaultConfig.maxScore,
      feedbackConfig: {
        ...defaultConfig.feedbackConfig,
        ...testConfig.feedbackConfig
      }
    };
  }, [testType, testConfig]);
  const processedData = useMemo(() => {
    
    if (testType === "songs") {
      // Procesar datos del test de canciones
      if (!testData?.data?.puntaje_total && testData?.data?.puntaje_total !== 0) {
        return { "Resonancia Emocional": 0 };
      }

      const rawData = processSongTestData(testData);
      return rawData;
    }

    if (testType === "drawing") {
      // Procesar datos del test de dibujos
      if (!testData?.responses?.[0]?.total_score && testData?.responses?.[0]?.total_score !== 0) {
        return { "": 0 };
      }

      const rawData = processDrawingTestData(testData);
      return rawData;
    }
    
    // Procesar datos del test de valores usando la nueva estructura del backend
    let respuestas = null;
    let resultadoPorCategoria = null;
    
    // Verificar si vienen datos directos del backend (nueva estructura)
    if (testData?.success && testData?.data?.[0]) {
      const testDataItem = testData.data[0];
      respuestas = testDataItem.respuestas;
      resultadoPorCategoria = testDataItem.resultado_por_categoria;
    }
    // Verificar estructura antigua si existe
    else if (testData?.data?.[0]?.data?.[0]?.respuestas) {
      respuestas = testData.data[0].data[0].respuestas;
    }
    
    if (!respuestas || !Array.isArray(respuestas)) {
      return {
        "Principios Éticos": 0,
        "Conformidad Normativa": 0,
        "Apatía Social": 0,
        "Tendencia a la Corrupción": 0
      };
    }
    
    // Si ya tenemos los resultados calculados, usarlos directamente
    if (resultadoPorCategoria) {
      return {
        "Principios Éticos": resultadoPorCategoria["Principios Eticos"] || 0,
        "Conformidad Normativa": resultadoPorCategoria["Conformidad Normativa"] || 0,
        "Apatía Social": resultadoPorCategoria["Apatia Social"] || 0,
        "Tendencia a la Corrupción": resultadoPorCategoria["Tendencia a la Corrupcion"] || 0
      };
    }
    
    // Si no, calcular desde las respuestas
    const rawData = processValuesTestData(respuestas);
    
    // Mapear las claves para que coincidan con los títulos mostrados
    const mappedData = {
      "Principios Éticos": rawData["Principios Eticos"] || 0,
      "Conformidad Normativa": rawData["Conformidad Normativa"] || 0,
      "Apatía Social": rawData["Apatia Social"] || 0,
      "Tendencia a la Corrupción": rawData["Tendencia a la Corrupcion"] || 0
    };
    
    return mappedData;
  }, [testData, testType]);

  // Extraer interpretaciones del backend si están disponibles
  const backendInterpretations = useMemo(() => {
    if (testType === "drawing" && testData?.responses?.[0]?.classification_description) {
      return {
        "": testData.responses[0].classification_description
      };
    }
    
    if (testData?.success && testData?.data?.[0]?.interpretaciones) {
      const interpretaciones = testData.data[0].interpretaciones;
      return {
        "Principios Éticos": interpretaciones["Principios Eticos"] || null,
        "Conformidad Normativa": interpretaciones["Conformidad Normativa"] || null,
        "Apatía Social": interpretaciones["Apatia Social"] || null,
        "Tendencia a la Corrupción": interpretaciones["Tendencia a la Corrupcion"] || null
      };
    }
    return {};
  }, [testData, testType]);
  
  return (
    <MDBox sx={{ backgroundColor: '#f0f0f0', padding: '20px', marginBottom: '20px' }}>
      <MDTypography variant="h4" textAlign="center" fontWeight="bold" color="info" mb={3}>
        {config.title}
      </MDTypography>
      
      <MDTypography variant="body2" textAlign="center" mb={3} color="text">
        {config.subtitle}
      </MDTypography>
      
      <Grid container spacing={3} sx={{ minHeight: '400px' }} justifyContent="center">
        {config.categories.map((category, index) => {
          const isInverseCategory = config.inverseCategories && config.inverseCategories.includes(category);
          
          return (
            <Grid 
              item 
              xs={12} 
              md={testType === "songs" || testType === "drawing" ? 6 : 6} 
              key={index}
              sx={testType === "songs" || testType === "drawing" ? { display: 'flex', justifyContent: 'center' } : {}}
            >
              <SingleAccelerometer 
                title={category}
                score={processedData[category] || 0}
                maxScore={config.maxScore}
                feedbackConfig={config.feedbackConfig}
                isInverseCategory={isInverseCategory}
                backendInterpretation={backendInterpretations[category]}
              />
            </Grid>
          );
        })}
      </Grid>
    </MDBox>
  );
}

ValuesTestAccelerometer.propTypes = {
  testData: PropTypes.oneOfType([
    // Values test structure
    PropTypes.shape({
      success: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.shape({
        id_respuestas: PropTypes.string,
        id_candidato: PropTypes.string,
        success: PropTypes.bool,
        data: PropTypes.arrayOf(PropTypes.shape({
          id_respuestas: PropTypes.string,
          id_candidato: PropTypes.string,
          respuestas: PropTypes.arrayOf(PropTypes.shape({
            opcion: PropTypes.string,
            categoria: PropTypes.string,
            valor_asignado: PropTypes.number,
            numero_pregunta: PropTypes.number
          })),
          created_at: PropTypes.string,
          updated_at: PropTypes.string
        })),
        respuestas: PropTypes.arrayOf(PropTypes.shape({
          opcion: PropTypes.string,
          categoria: PropTypes.string,
          valor_asignado: PropTypes.number,
          numero_pregunta: PropTypes.number
        })),
        created_at: PropTypes.string,
        updated_at: PropTypes.string
      }))
    }),
    // Songs test structure
    PropTypes.shape({
      data: PropTypes.shape({
        puntaje_total: PropTypes.number,
        por_tipo_perfil: PropTypes.object,
        por_fase: PropTypes.object,
        por_cancion: PropTypes.object
      }),
      message: PropTypes.string
    }),
    // Drawing test structure
    PropTypes.shape({
      responses: PropTypes.arrayOf(PropTypes.shape({
        id_respuesta: PropTypes.string,
        id_candidato: PropTypes.string,
        created_at: PropTypes.string,
        updated_at: PropTypes.string,
        most_preferred: PropTypes.string,
        second_preferred: PropTypes.string,
        total_score: PropTypes.number,
        classification: PropTypes.string,
        classification_description: PropTypes.string,
        interpretation: PropTypes.string
      }))
    })
  ]),
  testType: PropTypes.oneOf(["values", "songs", "drawing"]),
  testConfig: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string),
    maxScore: PropTypes.number,
    feedbackConfig: PropTypes.object
  }) 
};

export default ValuesTestAccelerometer;